import React from 'react';
import { Clock, Zap } from 'lucide-react';
import api from '../../lib/api';
import { usePrepTime } from '../../hooks/usePrepTime';
import { useToast } from '../../hooks/use-toast';

const DURATION_MIN = 30;

// Kitchen-facing rush control: bump the quoted pickup time during a rush.
export const PrepTimeControl = ({ dark = false }) => {
  const { prep, reload } = usePrepTime(30000);
  const { toast } = useToast();

  const set = async (extraMinutes) => {
    try {
      const { data } = await api.post('/admin/prep-time', { extraMinutes, durationMinutes: DURATION_MIN });
      reload();
      toast({
        title: data.rushActive ? `Quoting ~${data.etaMinutes} min` : `Back to normal (~${data.etaMinutes} min)`,
        description: data.rushActive ? `Auto-clears in ${DURATION_MIN} minutes.` : '',
      });
    } catch (err) {
      toast({ title: 'Could not update prep time', description: err?.response?.data?.detail || '' });
    }
  };

  if (!prep) return null;
  const until = prep.rushUntil ? new Date(prep.rushUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;

  const btn = (label, mins, active) => (
    <button
      key={label}
      onClick={() => set(mins)}
      data-testid={`prep-${mins}`}
      className={`px-3 py-1.5 rounded-xl text-sm font-bold transition ${
        active
          ? 'bg-[#ED2E2E] text-white'
          : dark
            ? 'bg-white/10 text-white hover:bg-white/20'
            : 'bg-white text-[#0F1C2E] border border-gray-300 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      data-testid="prep-time-control"
      className={`flex flex-wrap items-center gap-2 rounded-2xl px-4 py-3 ${
        dark
          ? 'bg-white/[0.04] border border-white/10'
          : prep.rushActive ? 'bg-[#FDF3EC] border border-[#ED2E2E]/30' : 'bg-white shadow-sm'
      }`}
    >
      <div className={`flex items-center gap-2 mr-1 ${dark ? 'text-white' : 'text-[#0F1C2E]'}`}>
        {prep.rushActive ? <Zap size={16} className="text-[#ED2E2E]" /> : <Clock size={16} className="opacity-60" />}
        <span className="text-sm">Quoting</span>
        <span className="font-heading font-black text-xl tabular-nums" data-testid="prep-eta">~{prep.etaMinutes} min</span>
      </div>
      {prep.rushActive && (
        <span className={`text-xs mr-2 ${dark ? 'text-white/60' : 'text-gray-500'}`} data-testid="prep-rush-until">
          rush +{prep.extraMinutes} min · until {until}
        </span>
      )}
      {btn('+5 min', 5, prep.rushActive && prep.extraMinutes === 5)}
      {btn('+10 min', 10, prep.rushActive && prep.extraMinutes === 10)}
      {prep.rushActive && btn('Back to normal', 0, false)}
    </div>
  );
};
